import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { tokens } from "../theme";
import type { SignageScreenGroup } from "@shared/types/signage";

/**
 * Confirm deleting a group.
 *
 * ## Why it spells out what is NOT deleted
 *
 * "Delete group" sits one click from a list of screens, and the two words an
 * operator is weighing are "group" and "screens". Only one of those is
 * recoverable: a group is a name and a list of ids and can be rebuilt in a
 * minute; a screen is a paired television in a building. Saying "the screens
 * are not deleted" out loud costs one line and removes the hesitation that
 * otherwise makes people leave dead groups lying around forever.
 *
 * ## Why it counts the screens
 *
 * A group with 8 screens in it is a different decision from an empty one, and
 * the count is the only part of that an operator cannot see from the row they
 * clicked.
 */
export default function SignageGroupDeleteDialog({
  group,
  busy,
  onClose,
  onConfirm,
}: {
  /** The group to delete, or null when the dialog is closed. */
  group: SignageScreenGroup | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const count = group?.screenIds.length ?? 0;
  return (
    <Dialog open={group !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <Typography sx={{ flex: 1, fontSize: 19, fontWeight: 700 }}>
            Delete screen group?
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
          The group <b style={{ color: tokens.ink }}>“{group?.name}”</b> will be
          deleted.
        </Typography>

        <Box component="ul" sx={{ pl: 2.5, my: 2 }}>
          <Typography
            component="li"
            sx={{ fontSize: 14, color: tokens.inkMuted }}
          >
            {count} {count === 1 ? "screen is" : "screens are"} affected.
          </Typography>
          <Typography
            component="li"
            sx={{ fontSize: 14, color: tokens.inkMuted }}
          >
            The screens themselves are not deleted.
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" gap={1}>
          <WarningAmberIcon sx={{ fontSize: 20, color: tokens.error }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            This cannot be undone.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="error"
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? "Deleting…" : "Delete group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
